/* Glide */
import Glide from '@glidejs/glide'
import confetti from 'canvas-confetti'
window.Glide = Glide

/* Alpine JS */
import Alpine from 'alpinejs'
import focus from '@alpinejs/focus'
import intersect from '@alpinejs/intersect'
import studentSenate from './student-senate'
import flight67 from './flight-67'
import whenAiSoundsCaring from './when-ai-sounds-caring'
import mockAppStoreAiOrNot from './mock-app-store-ai-or-not'
import classifyingTogether from './classifying-together'
import { SHORT_ANSWER_DEADLINE_MS, SHORT_ANSWER_RECOVERY_DEADLINE_MS } from './trivia-grading.mjs'

Alpine.plugin(focus)
Alpine.plugin(intersect)
Alpine.data('studentSenate', studentSenate)
Alpine.data('flight67', flight67)
Alpine.data('whenAiSoundsCaring', whenAiSoundsCaring)
Alpine.data('mockAppStoreAiOrNot', mockAppStoreAiOrNot)
Alpine.data('classifyingTogether', classifyingTogether)
window.Alpine = Alpine

/**
 * Reports play activity and receives canonical server-side scores.
 */
const createTriviaTracker = (endpoints, initialAgeRange = null) => {
	let sessionUid = null
	let startData = null
	let csrfToken = null
	let starting = null
	let ageRange = initialAgeRange

	const token = async () => {
		if (csrfToken) return csrfToken

		// Fetched at runtime rather than rendered into the page, so the markup
		// stays safe to serve from a static cache.
		const response = await fetch(endpoints.sessionInfo, {
			headers: { Accept: 'application/json' },
		})
		const data = await response.json()
		csrfToken = data.csrfTokenValue

		return csrfToken
	}

	const post = async (url, body, { timeoutMs = null } = {}) => {
		const controller = timeoutMs ? new AbortController() : null
		const timeout = controller ? setTimeout(() => controller.abort(), timeoutMs) : null

		try {
			const response = await fetch(url, {
				method: 'POST',
				headers: {
					Accept: 'application/json',
					'Content-Type': 'application/json',
					'X-CSRF-Token': await token(),
					'X-Requested-With': 'XMLHttpRequest',
				},
				body: JSON.stringify(body),
				signal: controller?.signal,
			})

			const data = await response.json().catch(() => ({}))
			if (!response.ok) {
				// Servd's platform rate limit returns 429 without a JSON message.
				const fallback =
					response.status === 429
						? 'Lots of players right now. Please wait a moment and try again.'
						: `Trivia request failed: ${response.status}`
				const error = new Error(data.message || fallback)
				error.status = response.status
				throw error
			}

			return data
		} catch (error) {
			if (error.name === 'AbortError') {
				const timeoutError = new Error('Your response is still being analyzed.')
				timeoutError.code = 'TRIVIA_TIMEOUT'
				throw timeoutError
			}

			throw error
		} finally {
			if (timeout) clearTimeout(timeout)
		}
	}

	const start = async (gameId, selectedAgeRange = null) => {
		if (startData) return startData

		if (!starting) {
			ageRange = selectedAgeRange || ageRange
			starting = post(endpoints.start, { gameId, ageRange })
				.then((data) => {
					sessionUid = data.sessionUid
					startData = data
					return startData
				})
				.catch((error) => {
					starting = null
					throw error
				})
		}

		return starting
	}

	return {
		start(gameId, selectedAgeRange = null) {
			return start(gameId, selectedAgeRange)
		},

		async present(gameId, questionId) {
			await start(gameId)
			return post(endpoints.present, { sessionUid, questionId })
		},

		async answer(gameId, payload, options = {}) {
			await start(gameId)
			return post(endpoints.answer, { sessionUid, ...payload }, options)
		},

		async complete() {
			if (!sessionUid) throw new Error('Trivia session has not started.')
			return post(endpoints.complete, { sessionUid })
		},

		async submitScore(payload) {
			if (!sessionUid) throw new Error('Trivia session has not started.')
			return post(endpoints.submitScore, { sessionUid, ...payload })
		},
	}
}

const braineeImages = [
	'/images/trivia/Brainee/Brainee1.png',
	'/images/trivia/Brainee/Brainee2.png',
	'/images/trivia/Brainee/Brainee3.png',
]

Alpine.data('triviaGame', (data) => ({
	data,
	tracker: createTriviaTracker(data.endpoints || {}, data.ageRangeValue || null),
	screen: 'rules',
	current: 0,
	score: 0,
	answerReview: [],
	selected: null,
	selectedBool: null,
	shortText: '',
	shortFeedback: '',
	shortPoints: 0,
	shortGradingStatus: 'idle',
	unresolvedGrades: 0,
	answered: false,
	wasCorrect: false,
	questionTransitionState: 'idle',
	questionReady: false,
	startPending: false,
	answerPending: false,
	gameError: '',
	answerError: '',
	serverMaxScore: null,
	teamName: '',
	city: '',
	leaderboardSubmitting: false,
	leaderboardSubmitted: false,
	leaderboardError: '',
	fallbackImages: [],
	allowUnload: false,

	init() {
		this.assignFallbackImages()
		this.bindLeaveGuard()
	},

	destroy() {
		this.unbindLeaveGuard()
	},

	// True while the player is on a question (mcq, true/false, or short
	// response). Results and pre-game screens should not block navigation.
	get leaveGuardActive() {
		return this.screen === 'play'
	},

	bindLeaveGuard() {
		this._onBeforeUnload = (event) => {
			if (!this.leaveGuardActive || this.allowUnload) return

			event.preventDefault()
			event.returnValue = ''
		}

		this._onDocumentClick = (event) => {
			if (!this.leaveGuardActive || this.allowUnload || event.defaultPrevented) return
			if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return

			const link = event.target.closest('a[href]')
			if (!link || link.target === '_blank' || link.hasAttribute('download')) return

			let destination
			try {
				destination = new URL(link.href, window.location.href)
			} catch {
				return
			}

			if (destination.protocol !== 'http:' && destination.protocol !== 'https:') return
			if (
				destination.origin === window.location.origin &&
				destination.pathname === window.location.pathname &&
				destination.search === window.location.search
			) {
				return
			}

			if (window.confirm('You have a trivia game in progress. Are you sure you want to leave?')) {
				this.allowUnload = true
				return
			}

			event.preventDefault()
			event.stopPropagation()
		}

		window.addEventListener('beforeunload', this._onBeforeUnload)
		document.addEventListener('click', this._onDocumentClick, true)
	},

	unbindLeaveGuard() {
		if (this._onBeforeUnload) {
			window.removeEventListener('beforeunload', this._onBeforeUnload)
		}

		if (this._onDocumentClick) {
			document.removeEventListener('click', this._onDocumentClick, true)
		}
	},

	async startGame(ageRange = null) {
		if (this.startPending) return

		this.startPending = true
		this.gameError = ''

		try {
			const selectedAgeRange = ageRange || this.data.ageRangeValue
			if (!selectedAgeRange) {
				throw new Error('Choose an age range before starting the game.')
			}

			this.data.ageRangeValue = selectedAgeRange
			this.answerReview = []
			const started = await this.tracker.start(this.data.gameId, selectedAgeRange)
			if (Array.isArray(started.questions)) {
				this.data.questions = started.questions
				this.assignFallbackImages()
			}
			if (!this.data.questions.length) {
				throw new Error('This game does not have any playable questions.')
			}
			this.screen = 'play'
			await this.$nextTick()
			await this.presentCurrentQuestion()
		} catch (error) {
			this.gameError = error.message || 'Unable to start the game. Please try again.'
		} finally {
			this.startPending = false
		}
	},

	async presentCurrentQuestion() {
		this.questionReady = false
		this.answerError = ''

		try {
			await this.tracker.present(this.data.gameId, this.question.id)
			this.questionReady = true
			if (this.question.type === 'short') {
				this.shortText = this.storedShortSubmission(this.question.id)
			}
		} catch (error) {
			this.answerError = error.message || 'Unable to start this question. Please try again.'
		}
	},

	assignFallbackImages(initialPreviousFallback = null) {
		let previousFallback = initialPreviousFallback

		this.fallbackImages = this.data.questions.map((question) => {
			if (question.image) {
				previousFallback = null
				return null
			}

			const availableImages = braineeImages.filter((image) => image !== previousFallback)
			const image = availableImages[Math.floor(Math.random() * availableImages.length)]
			previousFallback = image
			return image
		})
	},

	get question() {
		return (
			this.data.questions[this.current] || {
				type: 'mcq',
				choices: [],
			}
		)
	},

	get total() {
		return this.data.questions.length
	},

	get questionDisplayImage() {
		return this.question.image || this.fallbackImages[this.current] || braineeImages[0]
	},

	get maxPoints() {
		return this.serverMaxScore ?? this.data.questions.length * 1000
	},

	get formattedScore() {
		return this.score.toLocaleString()
	},

	get progressPct() {
		return this.total ? Math.round(((this.current + 1) / this.total) * 100) : 0
	},

	get correctAnswerText() {
		if (this.question.type === 'mcq') {
			const correct = (this.question.choices || []).find((choice) => choice.id === this.question.correctChoiceId)
			return correct ? correct.label : ''
		}

		if (this.question.type === 'tf') {
			return this.question.correctBool ? 'True' : 'False'
		}

		return ''
	},

	rememberAnswer() {
		const question = this.question
		let userAnswer = ''
		let userAnswerImage = null
		let correctAnswer = ''
		let correctAnswerImage = null
		let isCorrect = null

		if (question.type === 'mcq') {
			const selected = (question.choices || [])[this.selected] || null
			const correct =
				(question.choices || []).find((choice) => choice.id === question.correctChoiceId) || null
			userAnswer = selected?.label || ''
			userAnswerImage = selected?.image || null
			correctAnswer = correct?.label || ''
			correctAnswerImage = correct?.image || null
			isCorrect = this.wasCorrect === true
		} else if (question.type === 'tf') {
			userAnswer = this.selectedBool ? 'True' : 'False'
			correctAnswer = question.correctBool ? 'True' : 'False'
			isCorrect = this.wasCorrect === true
		} else if (question.type === 'short') {
			userAnswer = this.shortText.trim()
		} else {
			return
		}

		const row = {
			questionId: question.id,
			number: question.number,
			type: question.type,
			prompt: question.prompt || '',
			userAnswer,
			userAnswerImage,
			correctAnswer,
			correctAnswerImage,
			isCorrect,
		}

		this.answerReview = [
			...this.answerReview.filter((item) => item.questionId !== row.questionId),
			row,
		].sort((a, b) => a.number - b.number)
	},

	get resultMessage() {
		const percentage = this.maxPoints ? this.score / this.maxPoints : 0

		if (percentage === 1) return 'Perfect score! You know your stuff.'
		if (percentage >= 0.7) return 'Great job!'
		if (percentage >= 0.4) return 'Nice effort—try again to improve.'
		return 'Keep practicing—give it another go!'
	},

	celebrate() {
		confetti({
			particleCount: 100,
			spread: 360,
			startVelocity: 30,
			origin: { x: 0.5, y: 0.45 },
			colors: ['#4773b7', '#0497fb', '#c0519e', '#f5c518', '#23a043'],
			disableForReducedMotion: true,
		})
	},

	resetAnswer() {
		this.selected = null
		this.selectedBool = null
		this.shortText = ''
		this.shortFeedback = ''
		this.shortPoints = 0
		this.shortGradingStatus = 'idle'
		this.answered = false
		this.wasCorrect = false
		this.answerPending = false
		this.answerError = ''
		this.questionReady = false
	},

	async choose(index) {
		if (this.answered || this.answerPending || !this.questionReady) return

		const choice = this.question.choices[index]
		if (!choice) return

		this.selected = index
		this.answerPending = true
		this.answerError = ''

		try {
			const result = await this.tracker.answer(this.data.gameId, {
				questionId: this.question.id,
				choiceId: choice.id,
			})
			this.applyAnswerKey(result)
			this.answered = true
			this.wasCorrect = result.isCorrect === true
			this.score += Number(result.pointsAwarded || 0)
		} catch (error) {
			this.selected = null
			this.answerError = error.message || 'Unable to save your answer. Please try again.'
		} finally {
			this.answerPending = false
		}
	},

	isCorrect(index) {
		const choice = this.question.choices[index]
		return this.answered && !!choice && choice.id === this.question.correctChoiceId
	},

	isWrongSelected(index) {
		return this.answered && index === this.selected && !this.isCorrect(index)
	},

	async chooseBool(value) {
		if (this.answered || this.answerPending || !this.questionReady) return

		this.selectedBool = value
		this.answerPending = true
		this.answerError = ''

		try {
			const result = await this.tracker.answer(this.data.gameId, {
				questionId: this.question.id,
				boolAnswer: value,
			})
			this.applyAnswerKey(result)
			this.answered = true
			this.wasCorrect = result.isCorrect === true
			this.score += Number(result.pointsAwarded || 0)
		} catch (error) {
			this.selectedBool = null
			this.answerError = error.message || 'Unable to save your answer. Please try again.'
		} finally {
			this.answerPending = false
		}
	},

	isBoolCorrect(value) {
		return this.answered && value === this.question.correctBool
	},

	isBoolWrongSelected(value) {
		return this.answered && value === this.selectedBool && value !== this.question.correctBool
	},

	applyAnswerKey(result) {
		if (result.correctChoiceId != null) {
			this.question.correctChoiceId = Number(result.correctChoiceId)
		}

		if (typeof result.correctBool === 'boolean') {
			this.question.correctBool = result.correctBool
		}

		if (typeof result.explanation === 'string') {
			this.question.explanation = result.explanation
		}
	},

	async submitShort() {
		if (this.answered || this.answerPending || !this.questionReady || !this.shortText.trim()) {
			return
		}

		this.answerPending = true
		this.answerError = ''
		this.shortFeedback = ''
		this.shortPoints = 0
		this.shortGradingStatus = 'analyzing'
		const questionId = this.question.id
		const submittedText = this.shortText.trim()
		this.storeShortSubmission(questionId, submittedText)

		try {
			const result = await this.tracker.answer(
				this.data.gameId,
				{
					questionId,
					textAnswer: submittedText,
				},
				{ timeoutMs: SHORT_ANSWER_DEADLINE_MS },
			)
			this.answered = true
			this.applyShortGrade(result)
			if ((result.gradingStatus || 'pending') === 'pending') {
				await this.settleShortAfterTimeout(questionId, submittedText)
			} else {
				this.clearShortSubmission(questionId)
			}
		} catch (error) {
			if (error.code === 'TRIVIA_TIMEOUT') {
				this.answered = true
				this.shortGradingStatus = 'pending'
				this.shortFeedback = 'We’re finishing this response before moving on…'
				await this.settleShortAfterTimeout(questionId, submittedText)
			} else {
				this.shortGradingStatus = 'idle'
				this.answerError = error.message || 'Unable to save your response. Please try again.'
			}
		} finally {
			this.answerPending = false
		}
	},

	shortSubmissionKey(questionId) {
		return `trivia-short-${this.data.gameId}-${questionId}`
	},

	storeShortSubmission(questionId, text) {
		try {
			localStorage.setItem(this.shortSubmissionKey(questionId), text)
		} catch {
			// The in-memory copy still supports retries for this page visit.
		}
	},

	storedShortSubmission(questionId) {
		try {
			return localStorage.getItem(this.shortSubmissionKey(questionId)) || ''
		} catch {
			return ''
		}
	},

	clearShortSubmission(questionId) {
		try {
			localStorage.removeItem(this.shortSubmissionKey(questionId))
		} catch {
			// Storage may be unavailable in private browsing contexts.
		}
	},

	// Grades arrive while their question is still on screen: Next stays
	// disabled until the answer request (and any recovery check) settles.
	applyShortGrade(result) {
		const status = result.gradingStatus || 'pending'

		if (status === 'graded') {
			this.shortGradingStatus = 'graded'
			this.shortPoints = Number(result.pointsAwarded || 0)
			this.shortFeedback = result.feedback || ''
			this.score += this.shortPoints
			return
		}

		if (status === 'failed') {
			this.shortGradingStatus = 'failed'
			this.shortFeedback =
				'We’re sorry—the server is busy and can’t provide feedback for this question right now. It won’t count toward your score.'
			return
		}

		this.shortGradingStatus = 'pending'
		this.shortFeedback = 'We’re finishing this response before moving on…'
	},

	async settleShortAfterTimeout(questionId, submittedText) {
		try {
			const result = await this.tracker.answer(
				this.data.gameId,
				{
					questionId,
					textAnswer: submittedText,
					skipShortGrading: true,
				},
				{ timeoutMs: SHORT_ANSWER_RECOVERY_DEADLINE_MS },
			)
			this.clearShortSubmission(questionId)
			this.applyShortGrade(result)
		} catch (error) {
			this.answered = false
			this.shortGradingStatus = 'idle'
			this.answerError = error.message || 'Unable to save your response. Please try again.'
		}
	},

	async fadeOut(reduceMotion) {
		if (reduceMotion) return

		this.questionTransitionState = 'hidden'
		await new Promise((resolve) => setTimeout(resolve, 360))
	},

	async fadeIn(reduceMotion) {
		if (reduceMotion) {
			this.questionTransitionState = 'idle'
			return
		}

		await this.$nextTick()
		await new Promise((resolve) => {
			requestAnimationFrame(() => requestAnimationFrame(resolve))
		})
		this.questionTransitionState = 'idle'
	},

	async next() {
		// Players must submit an answer before advancing.
		if (!this.answered || this.answerPending || this.questionTransitionState !== 'idle') return

		this.rememberAnswer()

		const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
		const hasNextQuestion = this.current + 1 < this.total

		window.scrollTo({
			top: 0,
			behavior: reduceMotion ? 'auto' : 'smooth',
		})

		await this.fadeOut(reduceMotion)

		if (hasNextQuestion) {
			this.current++
			this.resetAnswer()
			await this.fadeIn(reduceMotion)
			await this.presentCurrentQuestion()
			return
		}

		this.answerPending = true
		this.answerError = ''

		try {
			const result = await this.tracker.complete()
			this.score = Number(result.score || 0)
			this.serverMaxScore = Number(result.maxScore || 0)
			this.unresolvedGrades = Number(result.unresolvedGrades || 0)
			if (Array.isArray(result.review) && result.review.length) {
				this.answerReview = result.review
			}
			this.screen = 'results'
			await this.fadeIn(reduceMotion)

			if (this.score > 0) {
				setTimeout(() => this.celebrate(), reduceMotion ? 0 : 160)
			}
		} catch (error) {
			this.answerError = error.message || 'Unable to finish the game. Please try again.'
			await this.fadeIn(reduceMotion)
		} finally {
			this.answerPending = false
		}
	},

	async submitLeaderboardScore() {
		if (
			this.leaderboardSubmitting ||
			this.leaderboardSubmitted ||
			this.unresolvedGrades > 0 ||
			!this.teamName.trim() ||
			!this.city.trim()
		) {
			return
		}

		this.leaderboardSubmitting = true
		this.leaderboardError = ''

		try {
			await this.tracker.submitScore({
				teamName: this.teamName.trim(),
				city: this.city.trim(),
			})
			this.leaderboardSubmitted = true
		} catch (error) {
			this.leaderboardError = error.message || 'Unable to add your score to the leaderboard. Please try again.'
		} finally {
			this.leaderboardSubmitting = false
		}
	},
}))

Alpine.start()

/**
 * Accept HMR as per: https://vitejs.dev/guide/api-hmr.html
 */
if (import.meta.hot) {
	import.meta.hot.accept(() => {
		console.log('HMR')
	})
}
